// src/views/Admin/Locales/LocalPage/components/ToggleStateModal/ToggleStateModal.tsx

import React, { useState } from 'react';
import { HiOutlineExclamationCircle, HiOutlineXMark, HiOutlinePlay, HiOutlinePause } from 'react-icons/hi2';
import './ToggleStateModal.scss';

interface ToggleStateModalProps {
  isOpen: boolean;
  localName: string;
  isCurrentlyActive: boolean;
  usersCount: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const ToggleStateModal: React.FC<ToggleStateModalProps> = ({
  isOpen,
  localName,
  isCurrentlyActive,
  usersCount,
  onClose,
  onConfirm
}) => {
  const [isToggling, setIsToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsToggling(true);
    setError(null);
    
    try {
      await onConfirm();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al cambiar el estado del local');
    } finally {
      setIsToggling(false);
    }
  };

  const handleClose = () => {
    if (isToggling) return;
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const actionWord = isCurrentlyActive ? 'desactivar' : 'activar';
  const actionWordCapitalized = isCurrentlyActive ? 'Desactivar' : 'Activar';
  const newState = isCurrentlyActive ? 'inactivo' : 'activo';

  return (
    <div className="toggle-modal-overlay" onClick={handleClose}>
      <div className="toggle-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose} disabled={isToggling}>
          <HiOutlineXMark size={20} />
        </button>

        <div className={`modal-icon ${isCurrentlyActive ? 'deactivate' : 'activate'}`}>
          {isCurrentlyActive ? <HiOutlinePause size={28} /> : <HiOutlinePlay size={28} />}
        </div>

        <h2>{actionWordCapitalized} Local</h2>
        
        <div className="info-box">
          <p>
            Estás a punto de <strong>{actionWord}</strong> el local <strong>"{localName}"</strong>.
          </p>
          <p className="secondary">
            Esta acción también {isCurrentlyActive ? 'desactivará' : 'activará'} a los <strong>{usersCount} usuarios</strong> asociados a este local.
          </p>
        </div>

        <div className="state-preview">
          <div className="state-item">
            <span className="label">Estado actual:</span>
            <span className={`badge ${isCurrentlyActive ? 'active' : 'inactive'}`}>
              {isCurrentlyActive ? 'Activo' : 'Inactivo'}
            </span>
          </div>
          <div className="arrow">→</div>
          <div className="state-item">
            <span className="label">Nuevo estado:</span>
            <span className={`badge ${!isCurrentlyActive ? 'active' : 'inactive'}`}>
              {!isCurrentlyActive ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isToggling}
          >
            Cancelar
          </button>
          <button 
            className={`confirm-button ${isCurrentlyActive ? 'deactivate' : 'activate'}`}
            onClick={handleConfirm}
            disabled={isToggling}
          >
            {isToggling ? (
              <>
                <span className="spinner" />
                {isCurrentlyActive ? 'Desactivando...' : 'Activando...'}
              </>
            ) : (
              `${actionWordCapitalized} Local`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToggleStateModal;
